import { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, Alert, AlertTitle, Paper, CircularProgress, Select, MenuItem, FormControl } from '@mui/material';
import { ArrowBack, Save, ArrowForward } from '@mui/icons-material';
import { AdminBarcodeInput } from '~/components/AdminBarcodeInput';
import { validateBarcode } from '../../lib/barcode-validator';
import { getOrCreateDeviceSerial, getDeviceMetadata } from '~/lib/auth/device-setup.client';
import { Container } from '~/components/ui';

type SetupStep = "admin_barcode" | "employee_info";

export default function SetupPage() {
  const [step, setStep] = useState<SetupStep>("admin_barcode");
  const [adminBarcode, setAdminBarcode] = useState("");
  const [username, setUsername] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deviceSerial, setDeviceSerial] = useState<string | null>(null);
  const [isClient, setIsClient] = useState(false);

  // Ensure this only runs on client
  useEffect(() => {
    setIsClient(true);

    // Get device serial on mount
    if (typeof window !== "undefined") {
      const serial = getOrCreateDeviceSerial();
      setDeviceSerial(serial);

      // Check if device is already registered
      checkDeviceStatus(serial);
    }
  }, []);

  const checkDeviceStatus = async (serial: string) => {
    try {
      const response = await fetch(
        `/api/auth/check-device?device_serial=${encodeURIComponent(serial)}`
      );
      if (!response.ok) {
        // If API fails, just show the form - user can still register
        return;
      }
      const data = await response.json();

      if (data.registered && data.device) {
        // Device is registered - user needs to re-authenticate with admin barcode
        // to get a new token
        setStep("admin_barcode");
      }
    } catch (error) {
      console.error("[Setup] Failed to check device status:", error);
      // On error, just show the form - user can still register
    }
  };

  // Handle admin barcode verification
  const handleAdminBarcodeSubmit = async (e?: React.FormEvent, barcodeOverride?: string) => {
    if (e) {
      e.preventDefault();
    }
    setError("");

    // Use full barcode directly - should already be in format R2XXXXXXMXXXX
    const fullBarcode = barcodeOverride || adminBarcode;

    // Validate barcode format (not database lookup)
    const validation = validateBarcode(fullBarcode);
    if (!fullBarcode || fullBarcode.length !== 13 || !validation.valid) {
      setError(validation.error || "الرجاء إدخال باركود إداري صحيح");
      return;
    }

    if (!deviceSerial) {
      setError("فشل تحديد رقم الجهاز");
      return;
    }

    setIsSubmitting(true);

    try {
      // First check if device is already registered
      const checkResponse = await fetch(
        `/api/auth/check-device?device_serial=${encodeURIComponent(deviceSerial)}`
      );
      const checkData = await checkResponse.json();

      if (checkData.registered) {
        // Device is registered - get/refresh token using full barcode
        const tokenResponse = await fetch("/api/auth/get-device-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            device_serial: deviceSerial,
            admin_barcode: fullBarcode, // Use the validated full barcode
          }),
        });

        const tokenData = await tokenResponse.json();

        if (!tokenResponse.ok) {
          // Provide specific error message based on the error
          const errorMsg = tokenData.error || "فشل الحصول على رمز الجهاز";
          const technicalError = tokenData.technical_error;
          
          throw new Error(
            technicalError 
              ? `${errorMsg} (${technicalError})`
              : errorMsg
          );
        }

        // Success - store token in both sessionStorage (for immediate use) and localStorage (for persistence)
        if (typeof window !== "undefined") {
          sessionStorage.setItem("device_token_temp", tokenData.token);
          sessionStorage.setItem("cashier_name_temp", tokenData.cashier_name);
          localStorage.setItem("radiomisr_device_token", tokenData.token);
          localStorage.setItem("radiomisr_cashier_name", tokenData.cashier_name);
          window.location.href = "/";
        }
        return;
      }

      // Device not registered - verify admin barcode and proceed to employee info
      const response = await fetch("/api/auth/verify-admin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ admin_barcode: fullBarcode }), // Use the validated full barcode
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "الباركود الإداري غير صحيح");
      }

      // Admin barcode verified, proceed to employee info
      setStep("employee_info");
    } catch (err: any) {
      setError(err.message || "فشل التحقق من الباركود الإداري");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle employee registration
  const handleEmployeeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim()) {
      setError("الرجاء إدخال اسم المستخدم");
      return;
    }

    if (!deviceType) {
      setError("الرجاء اختيار نوع الجهاز");
      return;
    }

    if (!deviceSerial) {
      setError("فشل تحديد رقم الجهاز");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get device metadata
      const metadata = getDeviceMetadata();

      // Use the full barcode that's already in correct format
      const fullBarcode = adminBarcode;

      const response = await fetch("/api/auth/register-device", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_serial: deviceSerial,
          cashier_name: username.trim(),
          device_type: deviceType,
          metadata: metadata,
          admin_barcode: fullBarcode, // Include full admin barcode for verification
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "فشل تسجيل الجهاز");
      }

      // Success - store token in both sessionStorage (for immediate use) and localStorage (for persistence)
      // The device context will pick it up
      if (typeof window !== "undefined") {
        sessionStorage.setItem("device_token_temp", data.token);
        sessionStorage.setItem("cashier_name_temp", data.cashier_name);
        localStorage.setItem("radiomisr_device_token", data.token);
        localStorage.setItem("radiomisr_cashier_name", data.cashier_name);
        window.location.href = "/";
      }
    } catch (err: any) {
      setError(err.message || "فشل تسجيل الجهاز");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading during SSR
  if (!isClient) {
    return (
      <Container size="lg" padding>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body1" style={{ textAlign: "center" }}>جاري التحميل...</Typography>
        </Paper>
      </Container>
    );
  }

  // Admin barcode step
  if (step === "admin_barcode") {
    return (
      <Container size="lg" padding>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              borderRadius: 3,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            <Typography variant="h4" component="h1" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
              تسجيل الجهاز
            </Typography>


            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>خطأ</AlertTitle>
                {error}
              </Alert>
            )}

            {deviceSerial && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ direction: 'ltr', textAlign: 'left' }}>
                  <strong>Device Serial:</strong> {deviceSerial}
                </Typography>
              </Box>
            )}

            <Box
              component="form"
              onSubmit={handleAdminBarcodeSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                  الباركود الإداري <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box dir="ltr">
                  <AdminBarcodeInput
                    value={adminBarcode}
                    onChange={(newValue) => {
                      setAdminBarcode(newValue);
                      if (error) setError('');
                    }}
                    disabled={isSubmitting}
                    autoFocus
                    onValidationChange={(isValid) => {
                      if (isValid && error) setError('');
                    }}
                  />
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || adminBarcode.length !== 13}
                fullWidth
                sx={{ height: 56 }}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <ArrowBack />}
              >
                {isSubmitting ? 'جاري التحقق...' : 'متابعة'}
              </Button>
            </Box>
          </Paper>
      </Container>
    );
  }

  // Employee info step
  return (
    <Container size="lg" padding>
      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
          تسجيل الموظف والجهاز
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
          يرجى إدخال معلومات الموظف والجهاز
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>خطأ</AlertTitle>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleEmployeeSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              اسم المستخدم (الموظف) <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="مثال: أحمد محمد"
              fullWidth
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              نوع الجهاز <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth>
              <Select
                value={deviceType}
                onChange={(e) => setDeviceType(e.target.value)}
                disabled={isSubmitting}
                displayEmpty
              >
                <MenuItem value="" disabled>اختر نوع الجهاز</MenuItem>
                <MenuItem value="kiosk">كشك (Kiosk)</MenuItem>
                <MenuItem value="tablet">تابلت (Tablet)</MenuItem>
                <MenuItem value="desktop">سطح المكتب (Desktop)</MenuItem>
                <MenuItem value="mobile">موبايل (Mobile)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>رقم الجهاز:</strong> {deviceSerial}
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting || !username.trim() || !deviceType}
            fullWidth
            sx={{ height: 56 }}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'جاري التسجيل...' : 'تسجيل الجهاز'}
          </Button>

          <Button
            type="button"
            variant="outlined"
            size="large"
            onClick={() => setStep('admin_barcode')}
            disabled={isSubmitting}
            fullWidth
            sx={{ height: 56 }}
            startIcon={<ArrowForward />}
          >
            العودة للخلف
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
